import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getArtist, createArtist, updateArtist, deleteArtist, translateAdminFields, getArtistPayouts, createArtistPayout, deleteArtistPayout } from '../../services/adminService';
import ImageUploadField from '../../components/ImageUploadField';
import VideoUploadField from '../../components/VideoUploadField';
import CustomShowHistoryField from '../../components/CustomShowHistoryField';
import { labelStyle, inputStyle } from '../../components/adminFieldStyles';

const ARTIST_IMAGE_BUCKET = 'products_images';
const ARTIST_VIDEO_BUCKET = 'artists_videos';

const fieldStyle = { marginBottom: '1.1rem' };

// Spanish source fields that have EN/ZH/JA counterparts in the translations
// section, keyed by the form field holding the Spanish text. Their per-locale
// fields are `${base}_en` / `_zh` / `_ja`.
const TRANSLATABLE_FIELDS = ['about', 'creatorAbout', 'studioVideoCaption', 'rightsReserved', 'galleryHeading'];
const TRANSLATE_SUFFIXES = ['en', 'zh', 'ja'];

const Field = ({ label, required, children }) => (
  <div style={fieldStyle}>
    <label style={labelStyle}>
      {label}{required && <span style={{ color: '#E4405F', marginLeft: 2 }}>*</span>}
    </label>
    {children}
  </div>
);

const EMPTY = {
  name: '',
  creatorName: '',
  creatorAbout: '',
  creatorAbout_en: '',
  creatorAbout_zh: '',
  creatorAbout_ja: '',
  creatorProfileImageUrl: '',
  creatorAboutFirst: false,
  slug: '',
  about: '',
  about_en: '',
  about_zh: '',
  about_ja: '',
  rightsReserved: '',
  rightsReserved_en: '',
  rightsReserved_zh: '',
  rightsReserved_ja: '',
  galleryHeading: '',
  galleryHeading_en: '',
  galleryHeading_zh: '',
  galleryHeading_ja: '',
  accentColor: '',
  fontColor: '',
  nationality: '',
  profileImageUrl: '',
  coverImageUrl: '',
  showcaseImage1Url: '',
  showcaseImage2Url: '',
  showcaseImage3Url: '',
  studioVideoUrl: '',
  studioVideoCaption: '',
  studioVideoCaption_en: '',
  studioVideoCaption_zh: '',
  studioVideoCaption_ja: '',
  customShowHistory: [],
  websiteUrl: '',
  instagramHandle: '',
  verified: false,
  featured: false,
  artistEmail: '',
  status: 'active',
  profileChangeNotes: null,
  bankName: '',
  bankBeneficiary: '',
  bankClabe: '',
  // The artist authorized storing/sharing their bank details (required to save a CLABE).
  bankConsent: false,
  bankConsentAt: null,
};

function toSlug(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function formFromArtist(artist) {
  return {
    name: artist.name || '',
    creatorName: artist.creatorName || '',
    creatorAbout: artist.creatorAbout || '',
    creatorAbout_en: artist.creatorAbout_en || artist.creator_about_en || '',
    creatorAbout_zh: artist.creatorAbout_zh || artist.creator_about_zh || '',
    creatorAbout_ja: artist.creatorAbout_ja || artist.creator_about_ja || '',
    creatorProfileImageUrl: artist.creatorProfileImageUrl || '',
    creatorAboutFirst: artist.creatorAboutFirst ?? false,
    slug: artist.slug || '',
    about: artist.about || '',
    about_en: artist.about_en || '',
    about_zh: artist.about_zh || '',
    about_ja: artist.about_ja || '',
    rightsReserved: artist.rightsReserved || '',
    rightsReserved_en: artist.rights_reserved_en || artist.rightsReserved_en || '',
    rightsReserved_zh: artist.rights_reserved_zh || artist.rightsReserved_zh || '',
    rightsReserved_ja: artist.rights_reserved_ja || artist.rightsReserved_ja || '',
    galleryHeading: artist.galleryHeading || '',
    galleryHeading_en: artist.gallery_heading_en || artist.galleryHeading_en || '',
    galleryHeading_zh: artist.gallery_heading_zh || artist.galleryHeading_zh || '',
    galleryHeading_ja: artist.gallery_heading_ja || artist.galleryHeading_ja || '',
    accentColor: artist.accentColor || '',
    fontColor: artist.fontColor || '',
    nationality: artist.nationality || '',
    profileImageUrl: artist.profileImageUrl || '',
    coverImageUrl: artist.coverImageUrl || '',
    showcaseImage1Url: artist.showcaseImage1Url || '',
    showcaseImage2Url: artist.showcaseImage2Url || '',
    showcaseImage3Url: artist.showcaseImage3Url || '',
    studioVideoUrl: artist.studioVideoUrl || '',
    studioVideoCaption: artist.studioVideoCaption || '',
    studioVideoCaption_en: artist.studioVideoCaption_en || artist.studio_video_caption_en || '',
    studioVideoCaption_zh: artist.studioVideoCaption_zh || artist.studio_video_caption_zh || '',
    studioVideoCaption_ja: artist.studioVideoCaption_ja || artist.studio_video_caption_ja || '',
    customShowHistory: Array.isArray(artist.customShowHistory) ? artist.customShowHistory : [],
    websiteUrl: artist.websiteUrl || '',
    instagramHandle: artist.instagramHandle || '',
    verified: artist.verified ?? false,
    featured: artist.featured ?? false,
    artistEmail: artist.artistEmail || '',
    status: artist.status || 'active',
    profileChangeNotes: artist.profileChangeNotes || null,
    bankName: artist.bankName || '',
    bankBeneficiary: artist.bankBeneficiary || '',
    bankClabe: artist.bankClabe || '',
    bankConsent: Boolean(artist.bankConsentAt),
    bankConsentAt: artist.bankConsentAt || null,
    // Remembered so the form can warn when a save turns card payments off.
    savedBankClabe: artist.bankClabe || '',
  };
}

const PAYOUT_METHOD_LABELS = {
  bank_transfer: 'Transferencia bancaria',
  cash: 'Efectivo',
  other: 'Otro',
};

const PAYOUT_EMPTY_FORM = { amount: '', method: 'bank_transfer', reference: '', note: '', paidAt: '' };

// Manual payout ledger for one artist — logs money already sent, never
// calculates what's owed. Only rendered in edit mode (needs an artist id).
const ArtistPayoutsSection = ({ artistId, session }) => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(PAYOUT_EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadPayouts = () => {
    const token = session?.access_token;
    if (!token) return;
    setLoading(true);
    setLoadError(null);
    getArtistPayouts(token, { artist_id: artistId, limit: 50 })
      .then((data) => setPayouts(data.payouts || []))
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistId, session?.access_token]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddPayout = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    const parsed = parseFloat(form.amount);
    if (!parsed || parsed <= 0) {
      setSubmitError('Ingresa un monto válido mayor a 0.');
      return;
    }
    setSubmitting(true);
    try {
      await createArtistPayout(session.access_token, {
        artistId,
        amount: parsed,
        method: form.method,
        reference: form.reference.trim() || undefined,
        note: form.note.trim() || undefined,
        paidAt: form.paidAt ? new Date(form.paidAt).toISOString() : undefined,
      });
      setForm(PAYOUT_EMPTY_FORM);
      loadPayouts();
    } catch (err) {
      setSubmitError(err.message || 'No se pudo registrar el pago.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayout = async (payout) => {
    if (!window.confirm(`¿Eliminar el registro de pago de ${payout.currency} ${payout.amount}? Esta acción no se puede deshacer.`)) return;
    setDeletingId(payout.id);
    try {
      await deleteArtistPayout(session.access_token, payout.id);
      setPayouts((prev) => prev.filter((p) => p.id !== payout.id));
    } catch (err) {
      window.alert(err.message || 'No se pudo eliminar el pago.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatAmount = (amount, currency) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: (currency || 'MXN').toUpperCase() }).format(Number(amount || 0));

  return (
    <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '2rem', marginBottom: '2rem' }}>
      <h2 style={{ margin: '0 0 0.4rem', fontSize: '1rem', fontWeight: '600', color: '#333' }}>Pagos al artista</h2>
      <p style={{ margin: '0 0 1.5rem', fontSize: '0.82rem', color: '#888' }}>
        Registro manual de transferencias ya enviadas. No calcula automáticamente lo que se le debe al artista — revisa las métricas del artista y registra aquí el monto después de enviarlo.
      </p>

      {loading ? (
        <p style={{ color: '#888', fontSize: '0.9rem' }}>Cargando...</p>
      ) : loadError ? (
        <p style={{ color: '#dc3545', fontSize: '0.9rem' }}>{loadError}</p>
      ) : payouts.length === 0 ? (
        <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1.5rem' }}>Aún no hay pagos registrados.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee', color: '#888' }}>
                <th style={{ padding: '0.5rem 0.5rem 0.5rem 0' }}>Fecha</th>
                <th style={{ padding: '0.5rem' }}>Monto</th>
                <th style={{ padding: '0.5rem' }}>Método</th>
                <th style={{ padding: '0.5rem' }}>Referencia</th>
                <th style={{ padding: '0.5rem' }}>Nota</th>
                <th style={{ padding: '0.5rem' }}>Registró</th>
                <th style={{ padding: '0.5rem' }}></th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '0.5rem 0.5rem 0.5rem 0', whiteSpace: 'nowrap' }}>{new Date(p.paidAt).toLocaleDateString('es-MX')}</td>
                  <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{formatAmount(p.amount, p.currency)}</td>
                  <td style={{ padding: '0.5rem' }}>{PAYOUT_METHOD_LABELS[p.method] || p.method}</td>
                  <td style={{ padding: '0.5rem', color: '#666' }}>{p.reference || '—'}</td>
                  <td style={{ padding: '0.5rem', color: '#666' }}>{p.note || '—'}</td>
                  <td style={{ padding: '0.5rem', color: '#888' }}>{p.createdByEmail || '—'}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => handleDeletePayout(p)}
                      disabled={deletingId === p.id}
                      style={{ background: 'none', border: 'none', color: '#dc3545', fontSize: '0.8rem', cursor: deletingId === p.id ? 'default' : 'pointer' }}
                    >
                      {deletingId === p.id ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={handleAddPayout} style={{ borderTop: '1px solid #eee', paddingTop: '1.25rem' }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '0.85rem', fontWeight: '600', color: '#333' }}>Registrar nuevo pago</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0 1.5rem' }}>
          <Field label="Monto (MXN)" required>
            <input
              type="number"
              name="amount"
              value={form.amount}
              onChange={handleFormChange}
              min="0.01"
              step="0.01"
              placeholder="0.00"
              style={inputStyle}
            />
          </Field>
          <Field label="Método">
            <select name="method" value={form.method} onChange={handleFormChange} style={inputStyle}>
              {Object.entries(PAYOUT_METHOD_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </Field>
          <Field label="Fecha">
            <input
              type="date"
              name="paidAt"
              value={form.paidAt}
              onChange={handleFormChange}
              style={inputStyle}
            />
          </Field>
          <Field label="Referencia">
            <input
              type="text"
              name="reference"
              value={form.reference}
              onChange={handleFormChange}
              placeholder="Folio o confirmación"
              style={inputStyle}
            />
          </Field>
        </div>
        <Field label="Nota">
          <input
            type="text"
            name="note"
            value={form.note}
            onChange={handleFormChange}
            placeholder="Opcional"
            style={inputStyle}
          />
        </Field>

        {submitError && <p style={{ color: '#dc3545', fontSize: '0.85rem', margin: '0 0 1rem' }}>{submitError}</p>}

        <button
          type="submit"
          disabled={submitting}
          style={{
            padding: '0.6rem 1.5rem',
            background: submitting ? '#ccc' : '#2ca9e1',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '0.9rem',
            cursor: submitting ? 'default' : 'pointer',
          }}
        >
          {submitting ? 'Registrando...' : 'Registrar pago'}
        </button>
      </form>
    </div>
  );
};

const AdminArtistForm = () => {
  const { id } = useParams();
  const { session } = useAuth();
  const navigate = useNavigate();

  const isNew = id === 'new';

  const [form, setForm]             = useState(EMPTY);
  const [slugEdited, setSlugEdited] = useState(false);
  const [loading, setLoading]       = useState(!isNew);
  const [fetchError, setFetchError] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [translating, setTranslating]       = useState(false);
  const [translateError, setTranslateError] = useState(null);
  const [translateNote, setTranslateNote]   = useState(null);
  const [deleting, setDeleting]     = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [profileUploading, setProfileUploading] = useState(false);
  const [coverUploading, setCoverUploading]     = useState(false);
  const [showcase1Uploading, setShowcase1Uploading] = useState(false);
  const [showcase2Uploading, setShowcase2Uploading] = useState(false);
  const [showcase3Uploading, setShowcase3Uploading] = useState(false);
  const [studioVideoUploading, setStudioVideoUploading] = useState(false);
  const [creatorPortraitUploading, setCreatorPortraitUploading] = useState(false);
  const [customHistoryUploading, setCustomHistoryUploading] = useState(false);
  const isUploading = profileUploading || coverUploading || showcase1Uploading || showcase2Uploading || showcase3Uploading || studioVideoUploading || creatorPortraitUploading || customHistoryUploading;

  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; });

  useEffect(() => {
    if (isNew) return;

    const token = sessionRef.current?.access_token;
    if (!token) {
      setLoading(false);
      setFetchError('No hay sesión activa.');
      return;
    }

    let cancelled = false;
    setLoading(true);

    getArtist(token, id)
      .then((data) => {
        if (!cancelled) {
          setForm(formFromArtist(data.artist));
          setSlugEdited(true); // lock slug in edit mode
        }
      })
      .catch((err) => {
        if (!cancelled) setFetchError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [id, isNew]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, name: value }));
  };

  const handleSlugChange = (e) => {
    setSlugEdited(true);
    setForm((prev) => ({ ...prev, slug: e.target.value }));
  };

  // Auto-generates the slug from whichever name is "primary" — the artist
  // name when creatorAboutFirst is checked, the project name otherwise —
  // until the admin edits the slug by hand (handleSlugChange above sets
  // slugEdited, which permanently disables this for the rest of the session).
  // Edit mode locks slugEdited to true on load (see the getArtist effect
  // below) so toggling creatorAboutFirst on an existing artist never rewrites
  // its live slug/URL.
  useEffect(() => {
    if (slugEdited) return;
    const source = form.creatorAboutFirst ? form.creatorName : form.name;
    const nextSlug = toSlug(source);
    setForm((prev) => (prev.slug === nextSlug ? prev : { ...prev, slug: nextSlug }));
  }, [form.creatorAboutFirst, form.name, form.creatorName, slugEdited]);

  // Machine-translate the Spanish source fields into EN/ZH/JA via Claude
  // (POST /api/admin/translate). `overwrite=false` fills only empty target
  // fields; `overwrite=true` replaces every target. Nothing is saved here —
  // the admin reviews the filled-in fields and submits the form as usual.
  const handleTranslate = async (overwrite) => {
    setTranslateError(null);
    setTranslateNote(null);

    const fields = {};
    for (const key of TRANSLATABLE_FIELDS) {
      const v = form[key]?.trim();
      if (v) fields[key] = v;
    }
    if (Object.keys(fields).length === 0) {
      setTranslateError('Escribe primero la descripción en español (About, Sobre el artista, etc.).');
      return;
    }

    const token = session?.access_token;
    if (!token) {
      setTranslateError('No hay sesión activa.');
      return;
    }

    setTranslating(true);
    try {
      const { translations } = await translateAdminFields(token, fields);
      let filled = 0;
      setForm((prev) => {
        const next = { ...prev };
        for (const suffix of TRANSLATE_SUFFIXES) {
          const t = translations[suffix] || {};
          for (const key of TRANSLATABLE_FIELDS) {
            const target = `${key}_${suffix}`;
            if (t[key] && (overwrite || !prev[target]?.trim())) {
              next[target] = t[key];
              filled += 1;
            }
          }
        }
        return next;
      });
      setTranslateNote(
        filled > 0
          ? `Traducción lista — ${filled} ${filled === 1 ? 'campo actualizado' : 'campos actualizados'}. Revisa y guarda.`
          : 'No había campos vacíos para rellenar. Usa "Volver a traducir todo" para sobrescribir.',
      );
    } catch (err) {
      setTranslateError(err.message || 'No se pudo traducir. Inténtalo de nuevo.');
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const payload = {
      name: form.name.trim(),
      creatorName: form.creatorName.trim() || undefined,
      creatorAbout: form.creatorAbout.trim() || undefined,
      creatorAbout_en: form.creatorAbout_en.trim() || null,
      creatorAbout_zh: form.creatorAbout_zh.trim() || null,
      creatorAbout_ja: form.creatorAbout_ja.trim() || null,
      creatorProfileImageUrl: form.creatorProfileImageUrl.trim() || undefined,
      creatorAboutFirst: form.creatorAboutFirst,
      slug: form.slug.trim(),
      about: form.about.trim() || undefined,
      about_en: form.about_en.trim() || null,
      about_zh: form.about_zh.trim() || null,
      about_ja: form.about_ja.trim() || null,
      rightsReserved: form.rightsReserved.trim() || undefined,
      rightsReserved_en: form.rightsReserved_en.trim() || null,
      rightsReserved_zh: form.rightsReserved_zh.trim() || null,
      rightsReserved_ja: form.rightsReserved_ja.trim() || null,
      galleryHeading: form.galleryHeading.trim() || undefined,
      galleryHeading_en: form.galleryHeading_en.trim() || null,
      galleryHeading_zh: form.galleryHeading_zh.trim() || null,
      galleryHeading_ja: form.galleryHeading_ja.trim() || null,
      accentColor: form.accentColor.trim() || undefined,
      fontColor: form.fontColor.trim() || undefined,
      nationality: form.nationality.trim() || undefined,
      profileImageUrl: form.profileImageUrl.trim() || undefined,
      coverImageUrl: form.coverImageUrl.trim() || undefined,
      showcaseImage1Url: form.showcaseImage1Url.trim() || undefined,
      showcaseImage2Url: form.showcaseImage2Url.trim() || undefined,
      showcaseImage3Url: form.showcaseImage3Url.trim() || undefined,
      studioVideoUrl: form.studioVideoUrl.trim() || undefined,
      studioVideoCaption: form.studioVideoCaption.trim() || undefined,
      studioVideoCaption_en: form.studioVideoCaption_en.trim() || null,
      studioVideoCaption_zh: form.studioVideoCaption_zh.trim() || null,
      studioVideoCaption_ja: form.studioVideoCaption_ja.trim() || null,
      customShowHistory: form.customShowHistory,
      websiteUrl: form.websiteUrl.trim() || undefined,
      instagramHandle: form.instagramHandle.trim() || undefined,
      verified: form.verified,
      featured: form.featured,
      artistEmail: form.artistEmail.trim() || null,
      bankName: form.bankName.trim() || null,
      bankBeneficiary: form.bankBeneficiary.trim() || null,
      bankClabe: form.bankClabe.trim() || null,
      bankConsent: form.bankConsent,
    };

    try {
      if (isNew) {
        await createArtist(session.access_token, payload);
        navigate('/admin/artists', { state: { created: true } });
      } else {
        const data = await updateArtist(session.access_token, id, payload);
        setForm(formFromArtist(data.artist));
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      setSaveError(err.message || 'No se pudo guardar el artista.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteArtist(session.access_token, id);
      navigate('/admin/artists');
    } catch (err) {
      if (err.message?.includes('productos asociados')) {
        setDeleteError('Este artista tiene productos asociados. Reasigna o elimina los productos antes de eliminar al artista.');
      } else {
        setDeleteError(err.message || 'No se pudo eliminar el artista.');
      }
      setDeleting(false);
    }
  };

  // One language's worth of translation fields (About, Rights reserved,
  // Studio caption, plus the creator bio when there's a creator name) —
  // rendered once per suffix (en/zh/ja) inside the shared translations
  // dropdown below. Nationality has no per-language variant — the single
  // "Nacionalidad" field above is shared across all locales.
  const renderTranslationFields = (suffix, langLabel) => (
    <div key={suffix}>
      <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
        {langLabel}
      </h4>
      <Field label={`About (${langLabel})`}>
        <textarea
          name={`about_${suffix}`}
          maxLength={2000}
          rows={4}
          value={form[`about_${suffix}`]}
          onChange={handleChange}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
        <span style={{ fontSize: '0.78rem', color: '#999', display: 'block', marginTop: '0.25rem' }}>
          {form[`about_${suffix}`].length}/2000 characters
        </span>
      </Field>
      <Field label={`About ${form.creatorName.trim() || 'the creator'} (${langLabel})`}>
        <textarea
          name={`creatorAbout_${suffix}`}
          maxLength={2000}
          rows={4}
          value={form[`creatorAbout_${suffix}`]}
          onChange={handleChange}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
        <span style={{ fontSize: '0.78rem', color: '#999', display: 'block', marginTop: '0.25rem' }}>
          {form[`creatorAbout_${suffix}`].length}/2000 characters
        </span>
      </Field>
      <Field label={`Studio caption (${langLabel}, optional)`}>
        <textarea
          name={`studioVideoCaption_${suffix}`}
          maxLength={400}
          rows={3}
          value={form[`studioVideoCaption_${suffix}`]}
          onChange={handleChange}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
        <span style={{ fontSize: '0.78rem', color: '#999', display: 'block', marginTop: '0.25rem' }}>
          {form[`studioVideoCaption_${suffix}`].length}/400 characters
        </span>
      </Field>
      <Field label={`Rights reserved (${langLabel}, optional)`}>
        <textarea
          name={`rightsReserved_${suffix}`}
          maxLength={400}
          rows={2}
          value={form[`rightsReserved_${suffix}`]}
          onChange={handleChange}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
        <span style={{ fontSize: '0.78rem', color: '#999', display: 'block', marginTop: '0.25rem' }}>
          {form[`rightsReserved_${suffix}`].length}/400
        </span>
      </Field>
      <Field label={`Encabezado de la sección de obras (${langLabel}, optional)`}>
        <input
          type="text"
          name={`galleryHeading_${suffix}`}
          maxLength={60}
          value={form[`galleryHeading_${suffix}`]}
          onChange={handleChange}
          placeholder="Art Toys"
          style={inputStyle}
        />
      </Field>
    </div>
  );

  if (loading) {
    return <div className="admin-loading">Cargando artista...</div>;
  }

  if (fetchError) {
    return (
      <div className="container" style={{ padding: '4rem 0' }}>
        <Link to="/admin/artists" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Artistas</Link>
        <p style={{ color: '#dc3545', marginTop: '1rem' }}>{fetchError}</p>
      </div>
    );
  }

  // The name fields and the about blocks below are order-swapped when
  // creatorAboutFirst is checked, so the admin form mirrors the public page
  // layout (see ArtistPage.js) instead of always editing the project info first.
  const projectNameField = (
    <Field label="Nombre del proyecto" required>
      <input
        type="text"
        name="name"
        required
        maxLength={200}
        value={form.name}
        onChange={handleNameChange}
        style={inputStyle}
      />
    </Field>
  );

  const artistNameField = (
    <Field label='Nombre del artista'>
      <input
        type="text"
        name="creatorName"
        maxLength={200}
        value={form.creatorName}
        onChange={handleChange}
        placeholder="ej. Ana López"
        style={inputStyle}
      />
    </Field>
  );

  const aboutBlock = (
    <Field label={form.creatorName.trim() ? "Descripción de la marca o proyecto" : "Descripción"}>
      <textarea
        name="about"
        maxLength={2000}
        rows={4}
        value={form.about}
        onChange={handleChange}
        style={{ ...inputStyle, resize: 'vertical' }}
      />
      <span style={{ fontSize: '0.78rem', color: '#999', display: 'block', marginTop: '0.25rem' }}>
        {form.about.length}/2000 caracteres
      </span>
    </Field>
  );

  const creatorAboutBlock = (
    <>
      <Field label={`Sobre ${form.creatorName.trim() || 'el artista'}`}>
        <textarea
          name="creatorAbout"
          maxLength={2000}
          rows={4}
          value={form.creatorAbout}
          onChange={handleChange}
          placeholder={form.creatorName.trim() ? `Bio de ${form.creatorName.trim()}.` : 'Bio del creador.'}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
        <span style={{ fontSize: '0.78rem', color: '#999', display: 'block', marginTop: '0.25rem' }}>
          {form.creatorAbout.length}/2000 caracteres · se muestra como "Sobre el creador" en el perfil público, junto con la descripción de arriba.
        </span>
      </Field>
      <ImageUploadField
        label={`Foto de ${form.creatorName.trim() || 'el creador'} — cuadrada (1:1), mínimo 400×400 px. Se muestra a la derecha de la bio del creador.`}
        value={form.creatorProfileImageUrl}
        onChange={(url) => setForm((prev) => ({ ...prev, creatorProfileImageUrl: url }))}
        bucket={ARTIST_IMAGE_BUCKET}
        onUploadingChange={setCreatorPortraitUploading}
      />
    </>
  );

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      {/* Header */}
      <Link to="/admin/artists" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>
        ← Artistas
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', margin: '0.75rem 0 2rem' }}>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.5rem', color: '#333', margin: 0 }}>
          {isNew ? 'Nuevo artista' : form.name || 'Editar artista'}
        </h1>
        {!isNew && (
          <button
            onClick={() => setDeleteModal(true)}
            style={{
              padding: '0.5rem 1.1rem',
              background: 'none',
              border: '1.5px solid #dc3545',
              borderRadius: '8px',
              color: '#dc3545',
              fontWeight: '600',
              fontSize: '0.88rem',
              cursor: 'pointer',
            }}
          >
            Eliminar artista
          </button>
        )}
      </div>


      {saveSuccess && (
        <div style={{
          background: '#eaf7ed', color: '#28a745',
          border: '1px solid #c3e6cb', borderRadius: '8px',
          padding: '0.75rem 1rem', marginBottom: '1.5rem', fontSize: '0.9rem', fontWeight: '500',
        }}>
          Artista guardado correctamente.
        </div>
      )}

      {saveError && (
        <div style={{
          background: '#fdecea', color: '#dc3545',
          border: '1px solid #f5c6cb', borderRadius: '8px',
          padding: '0.75rem 1rem', marginBottom: '1.5rem', fontSize: '0.9rem',
        }}>
          {saveError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '2rem', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: '0 0 1.5rem', fontSize: '1rem', fontWeight: '600', color: '#333' }}>Información básica</h2>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', color: '#333', marginBottom: '1.5rem' }}>
            <input
              type="checkbox"
              name="creatorAboutFirst"
              checked={form.creatorAboutFirst}
              onChange={handleChange}
              style={{ width: 16, height: 16, marginTop: 2, cursor: 'pointer', accentColor: '#E4405F', flexShrink: 0 }}
            />
            <span>
              <strong>Mostrar artista primero</strong> — nombre, bio y foto del artista antes que los del proyecto, tanto aquí como en el perfil público.
            </span>
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 1.5rem' }}>
            {form.creatorAboutFirst ? (
              <>
                {artistNameField}
                {projectNameField}
              </>
            ) : (
              <>
                {projectNameField}
                {artistNameField}
              </>
            )}

            <Field label="Email del artista">
              <input
                type="email"
                name="artistEmail"
                value={form.artistEmail}
                onChange={handleChange}
                placeholder="artista@email.com"
                style={inputStyle}
              />
              <span style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.2rem', display: 'block' }}>
                Cuando alguien contacte al artista o realice una compra, recibirá una copia del correo de notificación.
              </span>
            </Field>

            <Field label={isNew ? 'Slug (auto-generado)' : 'Slug (no cambiar sin razón)'} required>
              <input
                type="text"
                name="slug"
                required
                maxLength={100}
                value={form.slug}
                onChange={handleSlugChange}
                placeholder="ej. daniel-arsham"
                style={{ ...inputStyle, fontFamily: 'monospace', background: !isNew ? '#fafafa' : 'white' }}
              />
              <span style={{ fontSize: '0.78rem', color: '#999', marginTop: '0.25rem', display: 'block' }}>
                Solo letras minúsculas, números y guiones. Cambiar el slug en modo edición rompe las URLs públicas.
              </span>
            </Field>

            <Field label="Nacionalidad">
              <input
                type="text"
                name="nationality"
                maxLength={100}
                value={form.nationality}
                onChange={handleChange}
                placeholder="ej. MX, US, JP"
                style={inputStyle}
              />
            </Field>
          </div>

          {form.creatorAboutFirst ? (
            <>
              {creatorAboutBlock}
              {aboutBlock}
            </>
          ) : (
            <>
              {aboutBlock}
              {creatorAboutBlock}
            </>
          )}

          <Field label='Texto de la sección "En el estudio"'>
            <textarea
              name="studioVideoCaption"
              rows={3}
              value={form.studioVideoCaption}
              onChange={handleChange}
              maxLength={400}
              placeholder='Ej. "Un vistazo al proceso detrás de las piezas de TZITZINIMEH."'
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>

          <Field label="Derechos reservados (opcional)">
            <textarea
              name="rightsReserved"
              maxLength={400}
              rows={2}
              value={form.rightsReserved}
              onChange={handleChange}
              placeholder="ej. © 2026 [Artista]. Todos los derechos reservados."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <span style={{ fontSize: '0.78rem', color: '#999', display: 'block', marginTop: '0.25rem' }}>
              {form.rightsReserved.length}/400 — Se muestra como cita en el hero cuando no hay manifiesto.
            </span>
          </Field>

          <Field label="Encabezado de la sección de obras (opcional)">
            <input
              type="text"
              name="galleryHeading"
              maxLength={60}
              value={form.galleryHeading}
              onChange={handleChange}
              placeholder="Art Toys"
              style={inputStyle}
            />
            <span style={{ fontSize: '0.78rem', color: '#999', display: 'block', marginTop: '0.25rem' }}>
              {form.galleryHeading.length}/60 — Reemplaza el título "Art Toys" que aparece sobre la galería de obras de este artista.
            </span>
          </Field>

          {/* Translations (English / Chinese / Japanese, optional) — collapsed by default */}
          <details style={{ margin: '0 0 1.25rem', padding: '0.75rem 1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', color: '#333' }}>
              Traducciones (English / 中文 / 日本語) — opcional
            </summary>
            <p style={{ fontSize: '0.85rem', color: '#666', margin: '0.5rem 0 0.75rem' }}>
              Si dejas alguno vacío, se traduce automáticamente al guardar (Claude Haiku).
              O traduce ahora con los botones de abajo para revisar el resultado antes de guardar.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', margin: '0 0 0.75rem' }}>
              <button
                type="button"
                onClick={() => handleTranslate(false)}
                disabled={translating}
                style={{
                  padding: '0.5rem 1rem', borderRadius: '8px', border: '1.5px solid #E4405F',
                  background: 'white', color: '#E4405F', fontWeight: 600, fontSize: '0.85rem',
                  cursor: translating ? 'default' : 'pointer', opacity: translating ? 0.6 : 1,
                }}
              >
                {translating ? 'Traduciendo…' : 'Traducir campos vacíos'}
              </button>
              <button
                type="button"
                onClick={() => handleTranslate(true)}
                disabled={translating}
                style={{
                  padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #ccc',
                  background: 'white', color: '#555', fontWeight: 600, fontSize: '0.85rem',
                  cursor: translating ? 'default' : 'pointer', opacity: translating ? 0.6 : 1,
                }}
              >
                Volver a traducir todo
              </button>
              <span style={{ fontSize: '0.78rem', color: '#999' }}>English · 中文 · 日本語</span>
            </div>
            {translateError && (
              <p style={{ fontSize: '0.82rem', color: '#dc3545', margin: '0 0 1rem' }}>{translateError}</p>
            )}
            {translateNote && (
              <p style={{ fontSize: '0.82rem', color: '#28a745', margin: '0 0 1rem' }}>{translateNote}</p>
            )}
            {renderTranslationFields('en', 'English')}
            <hr style={{ border: 'none', borderTop: '1px solid #e9ecef', margin: '1.25rem 0' }} />
            {renderTranslationFields('zh', '中文')}
            <hr style={{ border: 'none', borderTop: '1px solid #e9ecef', margin: '1.25rem 0' }} />
            {renderTranslationFields('ja', '日本語')}
          </details>

          {/* Color pickers + live preview */}
          {(() => {
            const HEX_RE = /^#[0-9a-fA-F]{6}$/;
            const bg  = HEX_RE.test(form.accentColor) ? form.accentColor : '#1f2937';
            const fg  = HEX_RE.test(form.fontColor)   ? form.fontColor   : (() => {
              const r = parseInt(bg.slice(1,3),16), g = parseInt(bg.slice(3,5),16), b = parseInt(bg.slice(5,7),16);
              return (0.299*r + 0.587*g + 0.114*b)/255 > 0.5 ? '#000000' : '#ffffff';
            })();
            return (
              <div style={{ marginBottom: '1.1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                  {/* Background color */}
                  <div>
                    <label style={labelStyle}>Color de fondo (hex)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="color"
                        aria-label="Selector de color de fondo"
                        value={bg}
                        onChange={(e) => setForm((prev) => ({ ...prev, accentColor: e.target.value }))}
                        style={{ width: 44, height: 38, padding: 0, border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', background: 'none', flexShrink: 0 }}
                      />
                      <input
                        type="text"
                        name="accentColor"
                        maxLength={7}
                        value={form.accentColor}
                        onChange={handleChange}
                        placeholder="#1f2937"
                        style={{ ...inputStyle, fontFamily: 'monospace' }}
                      />
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#999', display: 'block', marginTop: '0.2rem' }}>
                      Vacío = paleta automática por nombre.
                    </span>
                  </div>

                  {/* Font color */}
                  <div>
                    <label style={labelStyle}>Color de texto (hex)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="color"
                        aria-label="Selector de color de texto"
                        value={fg}
                        onChange={(e) => setForm((prev) => ({ ...prev, fontColor: e.target.value }))}
                        style={{ width: 44, height: 38, padding: 0, border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', background: 'none', flexShrink: 0 }}
                      />
                      <input
                        type="text"
                        name="fontColor"
                        maxLength={7}
                        value={form.fontColor}
                        onChange={handleChange}
                        placeholder="auto"
                        style={{ ...inputStyle, fontFamily: 'monospace' }}
                      />
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#999', display: 'block', marginTop: '0.2rem' }}>
                      Vacío = blanco o negro automático según fondo.
                    </span>
                  </div>
                </div>

                {/* Live preview */}
                <div style={{
                  background: bg,
                  borderRadius: '10px',
                  padding: '1.25rem 1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                }}>
                  <span style={{
                    fontFamily: "'Gambado Sans', sans-serif",
                    fontSize: '1.5rem',
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: fg,
                    lineHeight: 1.1,
                  }}>
                    {form.name || 'Nombre del artista'}
                  </span>
                  {form.creatorName && (
                    <span style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.82rem',
                      fontWeight: 700,
                      color: fg,
                      letterSpacing: '0.04em',
                    }}>
                      por {form.creatorName}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '2rem', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: '0 0 0.4rem', fontSize: '1rem', fontWeight: '600', color: '#333' }}>Historial de customs</h2>
          <p style={{ margin: '0 0 1.5rem', fontSize: '0.82rem', color: '#888' }}>
            Sección opcional que aparece justo debajo de la bio en el perfil público, como línea de tiempo de customs o eventos.
          </p>
          <CustomShowHistoryField
            value={form.customShowHistory}
            onChange={(next) => setForm((prev) => ({ ...prev, customShowHistory: next }))}
            onUploadingChange={setCustomHistoryUploading}
          />
        </div>

        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '2rem', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: '0 0 1.5rem', fontSize: '1rem', fontWeight: '600', color: '#333' }}>Imágenes y redes</h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 1.5rem' }}>
            <ImageUploadField
              label="Imagen de perfil"
              value={form.profileImageUrl}
              onChange={(url) => setForm((prev) => ({ ...prev, profileImageUrl: url }))}
              bucket={ARTIST_IMAGE_BUCKET}
              onUploadingChange={setProfileUploading}
            />

            <ImageUploadField
              label="Imagen de portada"
              value={form.coverImageUrl}
              onChange={(url) => setForm((prev) => ({ ...prev, coverImageUrl: url }))}
              bucket={ARTIST_IMAGE_BUCKET}
              onUploadingChange={setCoverUploading}
            />

            <Field label="Sitio web">
              <input
                type="url"
                name="websiteUrl"
                maxLength={500}
                value={form.websiteUrl}
                onChange={handleChange}
                placeholder="https://..."
                style={inputStyle}
              />
            </Field>

            <Field label="Handle de Instagram (sin @)">
              <input
                type="text"
                name="instagramHandle"
                maxLength={100}
                value={form.instagramHandle}
                onChange={handleChange}
                placeholder="ej. kaws"
                style={inputStyle}
              />
            </Field>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '2rem', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: '0 0 0.4rem', fontSize: '1rem', fontWeight: '600', color: '#333' }}>Obras del showroom</h2>
          <p style={{ margin: '0 0 1.5rem', fontSize: '0.82rem', color: '#888' }}>
            Tres obras destacadas que se muestran como tríptico en el showroom público del artista. Si dejas alguna vacía, el showroom usará las que estén disponibles.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 1.5rem' }}>
            <ImageUploadField
              label="Obra 1"
              value={form.showcaseImage1Url}
              onChange={(url) => setForm((prev) => ({ ...prev, showcaseImage1Url: url }))}
              bucket={ARTIST_IMAGE_BUCKET}
              onUploadingChange={setShowcase1Uploading}
            />
            <ImageUploadField
              label="Obra 2"
              value={form.showcaseImage2Url}
              onChange={(url) => setForm((prev) => ({ ...prev, showcaseImage2Url: url }))}
              bucket={ARTIST_IMAGE_BUCKET}
              onUploadingChange={setShowcase2Uploading}
            />
            <ImageUploadField
              label="Obra 3"
              value={form.showcaseImage3Url}
              onChange={(url) => setForm((prev) => ({ ...prev, showcaseImage3Url: url }))}
              bucket={ARTIST_IMAGE_BUCKET}
              onUploadingChange={setShowcase3Uploading}
            />
          </div>

          <div style={{ marginTop: '0.5rem' }}>
            <VideoUploadField
              label="Video del estudio — formato vertical 1080×1920"
              value={form.studioVideoUrl}
              onChange={(url) => setForm((prev) => ({ ...prev, studioVideoUrl: url }))}
              bucket={ARTIST_VIDEO_BUCKET}
              onUploadingChange={setStudioVideoUploading}
              helperText='Loop corto (6–12 seg) en formato vertical 9:16. Se mostrará en la sección "En el estudio" del showroom; si lo dejas vacío esa sección no aparece.'
            />
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '2rem', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: '0 0 0.4rem', fontSize: '1rem', fontWeight: '600', color: '#333' }}>Datos bancarios</h2>
          <p style={{ margin: '0 0 1.5rem', fontSize: '0.82rem', color: '#888' }}>
            Solo visible para administradores. Los compradores que eligen transferencia reciben estos datos para pagarle
            directo al artista; también se usan para pagarle sus ventas con tarjeta. No aparecen en el showroom público.
            Obligatorios para subir piezas a la venta.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 1.5rem' }}>
            <Field label="Banco">
              <input
                type="text"
                name="bankName"
                value={form.bankName}
                onChange={handleChange}
                placeholder="ej. BBVA"
                style={inputStyle}
              />
            </Field>
            <Field label="Beneficiario">
              <input
                type="text"
                name="bankBeneficiary"
                value={form.bankBeneficiary}
                onChange={handleChange}
                placeholder="Nombre completo en la cuenta"
                style={inputStyle}
              />
            </Field>
            <Field label="CLABE">
              <input
                type="text"
                name="bankClabe"
                value={form.bankClabe}
                onChange={handleChange}
                placeholder="18 dígitos"
                maxLength={18}
                style={inputStyle}
              />
            </Field>
          </div>
          {form.bankClabe.trim() && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: '#333', marginTop: '0.5rem' }}>
              <input
                type="checkbox"
                name="bankConsent"
                checked={form.bankConsent}
                onChange={handleChange}
                style={{ width: 16, height: 16, marginTop: 2, cursor: 'pointer', accentColor: '#E4405F' }}
              />
              <span>
                El artista autorizó que Bluppimart guarde estos datos bancarios y los comparta con sus compradores
                para recibir pagos (Aviso de Privacidad).
                {form.bankConsentAt && (
                  <span style={{ color: '#888' }}> Autorizado el {new Date(form.bankConsentAt).toLocaleDateString('es-MX')}.</span>
                )}
              </span>
            </label>
          )}
          {form.bankClabe.trim() && !form.savedBankClabe && (
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.82rem', color: '#856404', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
              Al guardar una CLABE, las piezas de este artista dejan de cobrarse con tarjeta: los compradores le pagan
              directo por transferencia (o con el link de pago de cada pieza).
            </p>
          )}
        </div>

        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '2rem', marginBottom: '2rem' }}>
          <h2 style={{ margin: '0 0 1.5rem', fontSize: '1rem', fontWeight: '600', color: '#333' }}>Estado</h2>

          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', color: '#333' }}>
              <input
                type="checkbox"
                name="featured"
                checked={form.featured}
                onChange={handleChange}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#E4405F' }}
              />
              <span><strong>Destacado</strong> — aparece en secciones destacadas de la tienda</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', color: '#333' }}>
              <input
                type="checkbox"
                name="verified"
                checked={form.verified}
                onChange={handleChange}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#2ca9e1' }}
              />
              <span><strong>Verificado</strong> — muestra insignia de artista verificado</span>
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={saving || isUploading}
            style={{
              padding: '0.7rem 2rem',
              background: saving || isUploading ? '#ccc' : '#E4405F',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '1rem',
              cursor: saving || isUploading ? 'default' : 'pointer',
            }}
          >
            {saving ? 'Guardando...' : isUploading ? 'Subiendo archivo...' : isNew ? 'Crear artista' : 'Guardar cambios'}
          </button>
        </div>
      </form>

      {!isNew && <ArtistPayoutsSection artistId={id} session={session} />}

      {/* Delete confirmation modal */}
      {deleteModal && (
        <div
          onClick={() => !deleting && setDeleteModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              padding: '2rem', width: '100%', maxWidth: '420px',
            }}
          >
            <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.15rem', color: '#333' }}>
              ¿Eliminar artista?
            </h2>
            <p style={{ margin: '0 0 1rem', color: '#555', fontSize: '0.9rem' }}>
              Esta acción no se puede deshacer. Si el artista tiene productos asociados, la eliminación será rechazada.
            </p>

            {deleteError && (
              <p style={{ color: '#dc3545', fontSize: '0.88rem', marginBottom: '1rem' }}>{deleteError}</p>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDeleteModal(false); setDeleteError(null); }}
                disabled={deleting}
                style={{
                  padding: '0.6rem 1.25rem', background: 'none',
                  border: '1px solid #ddd', borderRadius: '8px',
                  fontSize: '0.9rem', cursor: deleting ? 'default' : 'pointer', color: '#555',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: deleting ? '#ccc' : '#dc3545',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontSize: '0.9rem', fontWeight: '600',
                  cursor: deleting ? 'default' : 'pointer',
                }}
              >
                {deleting ? 'Eliminando...' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminArtistForm;
